// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Skeleton from "@awc/App/Components/Skeleton/Skeleton";
import { pluralize } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        isEstimatedItemCount: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        // XNG-Changes
        isLoading: state.pages.productList.isLoading,
    };
};

// XNG-Changes: add HasCategoryContext
type Props = ReturnType<typeof mapStateToProps> & OwnProps & HasCategoryContext;

export interface ProductListProductCountStyles {
    wrapper?: InjectableCss;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
    queryText?: TypographyPresentationProps;
    // XNG-Changes
    textWrapper?: TypographyPresentationProps;
    resultsForText?: TypographyPresentationProps;
}

export const productCountStyles: ProductListProductCountStyles = {
    wrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    countText: {
        css: css`
            white-space: nowrap;
        `,
    },
    queryText: {
        css: css`
            white-space: nowrap;
            font-weight: bold;
        `,
    },
};

const styles = productCountStyles;

const ProductListProductCount: FC<Props> = ({ totalItemCount, query, category, isLoading, isEstimatedItemCount }) => {
    // XNG-Changes: add isLoading
    if (isLoading) {
        return <Skeleton isLoading={true} height="30px" {...styles.wrapper} />;
    }

    if (!totalItemCount) {
        return null;
    }

    // XNG-Changes: remove OOTB code
    // const itemsText =
    //     (totalItemCount !== 1 ? " items" : " item") +
    //     (isEstimatedItemCount ? " (estimated)" : "") +
    //     (query ? " for " : "");

    // XNG-Changes: structure
    return (
        <StyledWrapper {...styles.wrapper}>
            {query && (
                <StyledWrapper {...styles.textWrapper}>
                    <Typography {...styles.resultsForText}>{translate("Results For")}</Typography>
                    <Typography {...styles.queryText} data-test-selector="productListQueryText">
                        {query}
                    </Typography>
                </StyledWrapper>
            )}
            <Typography as="h2" {...styles.countText} data-test-selector="productListCountText">
                {pluralize(totalItemCount || 0, "item", { showNumber: true })}
            </Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(ProductListProductCount)),
    definition: {
        group: "Product List",
        displayName: "Product List Count",
        // XNG-Changes: add CategoryDetailsPage
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
